import { create } from 'zustand'

import { get as apiGet } from '@/api/request'
import type { MinerProduct } from '@/types/api'

export type MarketFilter = 'all' | 'growth'

// 记录已预热过的图片 URL，避免同一次会话重复创建 Image 实例
const preloadedImageUrls = new Set<string>()

function preloadMinerImages(products: MinerProduct[]): void {
  if (typeof window === 'undefined') {
    return
  }
  for (const p of products) {
    const url = p.imageUrl
    if (!url || preloadedImageUrls.has(url)) {
      continue
    }
    preloadedImageUrls.add(url)
    const img = new Image()
    // decoding=async + 无 onload 处理，纯触发网络请求并让浏览器入缓存
    img.decoding = 'async'
    img.src = url
  }
}

interface MarketStore {
  products: MinerProduct[]
  baseRatePerTHPerHour: number
  filter: MarketFilter
  isLoading: boolean
  error: string | null
  _initialized: boolean
  fetch: () => Promise<void>
  setFilter: (f: MarketFilter) => void
  reset: () => void
}

export const useMarketStore = create<MarketStore>((set, get) => ({
  products: [],
  baseRatePerTHPerHour: 0,
  filter: 'all',
  isLoading: false,
  error: null,
  _initialized: false,

  async fetch() {
    if (!get()._initialized) {
      set({ isLoading: true, error: null })
    }
    try {
      const res = await apiGet<{ products: MinerProduct[]; baseRatePerTHPerHour: number }>(
        '/miners/products',
      )
      if (res.data?.data) {
        set({
          products: res.data.data.products,
          baseRatePerTHPerHour: res.data.data.baseRatePerTHPerHour,
          error: null,
        })
        // 预热矿机产品图：主动把所有 imageUrl 塞入浏览器缓存，
        // 这样从其它 Tab 切回 Market 时，即便 Swiper 重新挂载，
        // <img> 也能同步命中 disk/memory cache 立即出图（iOS Safari
        // 对 Swiper 里 transform 屏外的 img 常会跳过 lazy 预取，
        // 这里做一次显式预加载是最稳妥的解法）。
        preloadMinerImages(res.data.data.products)
      }
    } catch (err) {
      if (!get()._initialized) {
        set({ error: err instanceof Error ? err.message : 'Failed' })
      }
    } finally {
      set({ isLoading: false, _initialized: true })
    }
  },

  setFilter(f) {
    set({ filter: f })
  },

  reset() {
    set({
      products: [],
      baseRatePerTHPerHour: 0,
      filter: 'all',
      isLoading: false,
      error: null,
      _initialized: false,
    })
  },
}))
